import React, { useState, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../utils/api';

const isEmailNotVerifiedError = (message) => {
  if (!message || typeof message !== 'string') return false;
  const lower = message.toLowerCase();
  return lower.includes('verify your email') || lower.includes('verification');
};

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const { login, getDashboardPath } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const showResendVerification = error && isEmailNotVerifiedError(error);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return t('common.emailRequired');
    }
    if (!emailRegex.test(email)) {
      return t('common.emailInvalid');
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) {
      return t('common.passwordRequired');
    }
    if (password.length < 6) {
      return t('common.passwordMinLength');
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (fieldErrors.email) {
      setFieldErrors({ ...fieldErrors, email: validateEmail(value) });
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (fieldErrors.password) {
      setFieldErrors({ ...fieldErrors, password: validatePassword(value) });
    }
  };

  const handleEmailBlur = () => {
    setFieldErrors({ ...fieldErrors, email: validateEmail(email) });
  };

  const handlePasswordBlur = () => {
    setFieldErrors({ ...fieldErrors, password: validatePassword(password) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    if (emailError || passwordError) {
      setFieldErrors({ email: emailError, password: passwordError });
      return;
    }

    setLoading(true);
    setResendSuccess(false);
    setResendError('');
    try {
      const loggedInUser = await login(email, password);
      // Check if there's a redirect in location state (from like button)
      if (location.state?.redirectTo && location.state?.redirectView) {
        navigate(location.state.redirectTo, { state: { view: location.state.redirectView } });
      } else {
        // Determine dashboard path based on the returned user
        const roleMap = {
          super_admin: '/super-admin',
          admin: '/admin',
          operations: '/operations',
          property_owner: '/owner',
          client: '/client',
          housekeeping: '/housekeeping',
        };
        const dashboardPath = roleMap[loggedInUser?.role] || '/client';
        navigate(dashboardPath);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const successMessage = location.state?.success;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
      <Navbar />
      
      <div className="flex items-center justify-center min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          {/* Logo Section */}
          <div className="text-center mb-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              {/* Logo */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="flex items-center justify-center p-1.5 rounded-lg" style={{ backgroundColor: '#FFFFFF' }}>
                  <img 
                    src="/hg-logo.png" 
                    alt="HOSTA GLOBAL Logo" 
                    className="w-auto"
                    style={{ maxHeight: '26px', objectFit: 'contain' }}
                  />
                </div>
                <div className="flex flex-col text-left">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>HOSTA</span>
                    <span className="text-2xl font-bold leading-tight uppercase" style={{ color: '#D5B577', fontFamily: 'serif' }}>GLOBAL</span>
                  </div>
                  <span className="text-[10px] leading-tight uppercase tracking-wide" style={{ color: '#D5B577', opacity: 0.8 }}>ELEVATING ASSETS | REDEFINING STAYS</span>
                </div>
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{t('login.title')}</h1>
            <p className="text-gray-400">{t('login.subtitle')}</p>
          </div>

          {/* Auth Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border p-5 sm:p-6" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
            {location.state?.success && (
              <div className="mb-6 p-4 rounded-xl bg-green-500/20 border border-green-500/50 text-green-200 text-sm">
                {location.state.success}
              </div>
            )}
            {error && (
              showResendVerification ? (
                <div className="mb-6 p-4 rounded-xl border flex flex-col gap-3 text-sm" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.4)' }}>
                  <div className="flex items-center gap-2" style={{ color: '#f59e0b' }}>
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{t('login.emailNotVerified') || 'Email not verified'}</span>
                  </div>
                  <p className="text-white/90">{error}</p>
                  {resendSuccess && <p className="text-green-400 text-sm">{t('login.resendSuccess') || 'Verification email sent. Please check your inbox and spam folder.'}</p>}
                  {resendError && <p className="text-red-400 text-sm">{resendError}</p>}
                  <button
                    type="button"
                    disabled={resendLoading}
                    onClick={async () => {
                      if (!email) return;
                      setResendError('');
                      setResendSuccess(false);
                      setResendLoading(true);
                      try {
                        await api.post('/api/clients/resend-verification', { email: email.trim() });
                        setResendSuccess(true);
                      } catch (err) {
                        setResendError(err.response?.data?.message || err.message || (t('login.resendFailed') || 'Failed to resend. Try again later.'));
                      } finally {
                        setResendLoading(false);
                      }
                    }}
                    className="w-full py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60"
                    style={{ borderColor: 'rgba(213, 181, 119, 0.6)', color: '#D5B577', backgroundColor: 'rgba(213, 181, 119, 0.1)' }}
                  >
                    {resendLoading ? (t('common.loading') || 'Sending...') : (t('login.resendVerification') || 'Resend verification email')}
                  </button>
                </div>
              ) : (
                <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-300 text-sm flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              )
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('login.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
            <input
              type="email"
              value={email}
                    onChange={handleEmailChange}
                    placeholder={t('login.email')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.email ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                    onBlur={(e) => {
                      handleEmailBlur();
                      if (!fieldErrors.email) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
              required
            />
          </div>
                {fieldErrors.email && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: '#D5B577' }}>
                  {t('login.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
            <input
                    type={showPassword ? 'text' : 'password'}
              value={password}
                    onChange={handlePasswordChange}
                    placeholder={t('login.password')}
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg focus:outline-none focus:ring-2 transition-all text-white placeholder-gray-400 text-sm"
                    style={{
                      backgroundColor: 'rgba(29, 42, 59, 0.7)',
                      border: fieldErrors.password ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(213, 181, 119, 0.3)',
                      height: '42px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D5B577'}
                    onBlur={(e) => {
                      handlePasswordBlur();
                      if (!fieldErrors.password) {
                        e.target.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                      }
                    }}
              required
            />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    style={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-0.5 text-xs text-red-400">{fieldErrors.password}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 rounded"
                    style={{ accentColor: '#D5B577' }}
                  />
                  <span className="ml-2 text-xs text-gray-300">{t('login.rememberMe')}</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs transition-colors"
                  style={{ color: '#D5B577' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {t('login.forgotPassword')}
                </Link>
          </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-base disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#c4a366')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = '#D5B577')}
              >
                {loading ? t('common.loading') || 'Loading...' : t('login.signIn')}
          </button>
        </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm mb-4">
                {t('login.noAccount')}{' '}
                <Link
                  to="/register"
                  className="font-semibold transition-colors"
                  style={{ color: '#D5B577' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  {t('login.signUp')}
                </Link>
        </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
